import { useEffect, useMemo, useRef, useState } from 'react';
import { buildDateMeta } from '../../utils/dateUtils';
import { getSearchHighlightColor, SEARCH_HIGHLIGHT_COLORS } from '../../utils/searchHighlights';
import { formatList, pickFirstValue } from '../../utils/resultsTableFormat';

const useResultsTableData = ({ data, selectedBrandCodes, fetchDiAccountsByBac, fetchDiOrderItemsLiveByBac, showToast }) => {
  const [rowOverrides, setRowOverrides] = useState({});
  const [diErrorShown, setDiErrorShown] = useState(false);
  const diLoadedBacsRef = useRef(new Set());
  const [appsheetErrorShown, setAppsheetErrorShown] = useState(false);
  const appsheetLoadedBacsRef = useRef(new Set());

  const preparedRows = useMemo(() => {
    return (data || [])
      .filter(Boolean)
      .map((row, index) => {
        const vendorSummary = Array.isArray(row.vendors)
          ? row.vendors
              .map(
                (vendor) =>
                  `${vendor.vendorName || 'Unknown'} (${vendor.vendorCd || 'N/A'}) - ${
                    vendor.vendorStatus || 'Unknown'
                  }${vendor.categoryCd ? ` [${vendor.categoryCd}]` : ''}`
              )
              .join('\n')
          : '';
        const matchedTerm =
          typeof row.matchedTerm === 'string' && row.matchedTerm.trim().length > 0
            ? row.matchedTerm.trim()
            : null;
        const matchedTermIndex =
          typeof row.matchedTermIndex === 'number' && Number.isInteger(row.matchedTermIndex)
            ? row.matchedTermIndex
            : null;
        const idParts = [
          row?.bac != null ? String(row.bac) : null,
          row?.programCode || row?.program || row?.programName || null,
          matchedTermIndex != null ? `term-${matchedTermIndex}` : null,
        ].filter(Boolean);
        const id = idParts.length > 0 ? idParts.join('-') : `row-${index}`;

        const resolvedBrandNames = Array.isArray(row.brandNames)
          ? row.brandNames
          : Array.isArray(row.brandName)
            ? row.brandName
            : row.brandName
              ? [row.brandName]
              : [];
        const brandNamesDisplay = formatList(resolvedBrandNames);

        const enrollmentValue = pickFirstValue(row, ['enrollmentDate', 'enrolledDt', 'enrollmentDt']);
        const enrollmentMeta = buildDateMeta(enrollmentValue);
        const lastUpdateValue = pickFirstValue(row, ['lastUpdateDate', 'lastUpdateDt']);
        const lastUpdateMeta = buildDateMeta(lastUpdateValue);
        const terminationValue = pickFirstValue(row, [
          'terminationDate',
          'isTerminatedDate',
          'termDate',
          'terminationDt',
        ]);
        const terminationMeta = buildDateMeta(terminationValue);

        return {
          id,
          ...row,
          matchedTerm,
          matchedTermIndex,
          brandNames: resolvedBrandNames,
          brandNamesDisplay,
          vendorSummary,
          enrollmentDateResolved: enrollmentMeta.display || '',
          enrollmentDateSort: enrollmentMeta.sort,
          lastUpdateResolved: lastUpdateMeta.display || '',
          lastUpdateSort: lastUpdateMeta.sort,
          terminationDateResolved: terminationMeta.display || '',
          terminationDateSort: terminationMeta.sort,
          uiNotes: row.uiNotes || '',
        };
      });
  }, [data]);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      const bacs = Array.from(
        new Set(
          (preparedRows || [])
            .map((row) => (row?.bac != null ? String(row.bac).trim() : ''))
            .filter((value) => value.length > 0)
        )
      );
      if (!bacs.length) return;
      const missing = bacs.filter((bac) => !diLoadedBacsRef.current.has(bac));
      if (!missing.length) return;

      try {
        const result = await fetchDiAccountsByBac(missing);
        const byBac = result?.byBac || {};
        missing.forEach((bac) => diLoadedBacsRef.current.add(bac));
        if (!mounted) return;

        setRowOverrides((prev) => {
          const next = { ...(prev || {}) };
          (preparedRows || []).forEach((row) => {
            const bac = row?.bac != null ? String(row.bac).trim() : '';
            if (!bac) return;
            if (!missing.includes(bac)) return;
            const di = byBac?.[bac];
            const csv = di?.accountIdsCsv || '';
            next[row.id] = { ...(next[row.id] || {}), diAccountIdsCsv: csv };
          });
          return next;
        });
      } catch (error) {
        // Non-blocking: DI is an optional enrichment, so keep the GM table usable.
        if (!mounted) return;
        if (!diErrorShown) {
          setDiErrorShown(true);
          showToast(
            error?.response?.data?.message ||
              error?.message ||
              'Failed to load DI Account IDs (AppSheet cache unavailable).',
            'warning'
          );
        }
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [preparedRows, fetchDiAccountsByBac, diErrorShown, showToast]);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (typeof fetchDiOrderItemsLiveByBac !== 'function') return;
      const bacs = Array.from(
        new Set(
          (preparedRows || [])
            .map((row) => (row?.bac != null ? String(row.bac).trim() : ''))
            .filter((value) => value.length > 0)
        )
      );
      if (!bacs.length) return;
      const missing = bacs.filter((bac) => !appsheetLoadedBacsRef.current.has(bac));
      if (!missing.length) return;

      try {
        const result = await fetchDiOrderItemsLiveByBac(missing);
        const byBac = result?.byBac || {};
        missing.forEach((bac) => appsheetLoadedBacsRef.current.add(bac));
        if (!mounted) return;

        setRowOverrides((prev) => {
          const next = { ...(prev || {}) };
          (preparedRows || []).forEach((row) => {
            const bac = row?.bac != null ? String(row.bac).trim() : '';
            if (!bac) return;
            if (!missing.includes(bac)) return;
            const appsheet = byBac?.[bac];
            if (!appsheet) return;
            next[row.id] = {
              ...(next[row.id] || {}),
              hasLiveAppsheetProducts: Boolean(appsheet.hasLiveOrderItems),
              appsheetLiveOrderItemsCount: appsheet.liveOrderItemsCount ?? 0,
              appsheetTotalOrderItemsCount: appsheet.totalOrderItems ?? 0,
            };
          });
          return next;
        });
      } catch (error) {
        if (!mounted) return;
        if (!appsheetErrorShown) {
          setAppsheetErrorShown(true);
          showToast(
            error?.response?.data?.message ||
              error?.message ||
              'Failed to load AppSheet live order items.',
            'warning'
          );
        }
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [preparedRows, fetchDiOrderItemsLiveByBac, appsheetErrorShown, showToast]);

  const mergedRows = useMemo(() => {
    const overrides = rowOverrides || {};
    if (!overrides || Object.keys(overrides).length === 0) return preparedRows;
    return preparedRows.map((row) => (overrides[row.id] ? { ...row, ...overrides[row.id] } : row));
  }, [preparedRows, rowOverrides]);

  const brandOptions = useMemo(() => {
    const codes = new Set();
    mergedRows.forEach((row) => {
      if (Array.isArray(row.brandCodes)) {
        row.brandCodes
          .map((code) => String(code).trim())
          .filter(Boolean)
          .forEach((code) => codes.add(code));
      }
    });
    return Array.from(codes).sort();
  }, [mergedRows]);

  const brandFilteredRows = useMemo(() => {
    if (!selectedBrandCodes.length) return mergedRows;
    return mergedRows.filter((row) => {
      if (!Array.isArray(row.brandCodes) || row.brandCodes.length === 0) return false;
      const normalizedCodes = row.brandCodes.map((code) => String(code).trim()).filter(Boolean);
      if (normalizedCodes.length === 0) return false;
      return normalizedCodes.every((code) => selectedBrandCodes.includes(code));
    });
  }, [mergedRows, selectedBrandCodes]);

  const matchedTermColorMap = useMemo(() => {
    const map = new Map();
    brandFilteredRows.forEach((row) => {
      if (typeof row?.matchedTermIndex !== 'number') return;
      const index = row.matchedTermIndex;
      if (!Number.isInteger(index)) return;
      if (map.has(index)) return;
      const paletteColor = getSearchHighlightColor(index) || SEARCH_HIGHLIGHT_COLORS[0];
      map.set(index, paletteColor);
    });
    return map;
  }, [brandFilteredRows]);

  const matchedTermLegends = useMemo(() => {
    const legends = [];
    matchedTermColorMap.forEach((baseColor, index) => {
      const representative = brandFilteredRows.find((entry) => entry?.matchedTermIndex === index);
      const term = representative?.matchedTerm || `Term ${index + 1}`;
      legends.push({ index, term, color: baseColor });
    });
    return legends.sort((a, b) => a.index - b.index);
  }, [brandFilteredRows, matchedTermColorMap]);

  return {
    preparedRows,
    mergedRows,
    brandOptions,
    brandFilteredRows,
    matchedTermLegends,
    matchedTermColorMap,
    rowOverrides,
    setRowOverrides,
  };
};

export default useResultsTableData;
